import {
    collection,
    collectionGroup,
    doc,
    onSnapshot,
    serverTimestamp,
    setDoc,
    Timestamp,
    updateDoc,
} from 'firebase/firestore';

import { db } from '../firebase.js';
import { companySlug } from './signupLink.js';

// Sign-up links live under companies/{companyId}/signup_links/{secret}. The link's URL carries
// "<companyId>-<secret>" so the redeem function can find it without searching every company.
const SIGNUP_BASE_URL = 'https://insights.aris-robotics.com/signup';

export const SIGNUP_TARGETS = {
    insights: { label: 'Insights', url: 'https://insights.aris-robotics.com/' },
    app: { label: 'Farligt affald-appen', url: 'https://wade-mobile.web.app/' },
};

export const SIGNUP_LINK_PERMISSIONS = [
    { key: 'can_view_all_company_data', label: 'Kan se al virksomhedsdata' },
    { key: 'can_view_physical_machine_data', label: 'Kan se data fra fysiske maskiner' },
    { key: 'can_view_waste_receiver_sorting_list', label: 'Kan se sorteringsliste (kun læsning)' },
];

const toDate = (value) => (value instanceof Timestamp ? value.toDate() : value);

function normalizeSignupLink(snapshot) {
    const data = snapshot.data();
    return {
        ...data,
        id: snapshot.id,
        companyId: snapshot.ref?.parent?.parent?.id,
        start_date: toDate(data.start_date),
        end_date: toDate(data.end_date),
        created_at: toDate(data.created_at),
        users_signed_up: (data.users_signed_up || []).map((signup) => ({
            ...signup,
            signed_up_at: toDate(signup.signed_up_at),
        })),
    };
}

const signupLinksCollection = (companyId) => collection(db, 'companies', companyId, 'signup_links');

export function subscribeToSignupLinks(companyId, onLinks, onError) {
    return onSnapshot(
        signupLinksCollection(companyId),
        (snapshot) => onLinks(snapshot.docs.map(normalizeSignupLink)),
        onError,
    );
}

// Every company's links at once, for the admin home page. Unfiltered on purpose: filtering a
// collection-group query needs an index of its own, and there are only ever a handful of links.
export function subscribeToAllSignupLinks(onLinks, onError) {
    return onSnapshot(
        collectionGroup(db, 'signup_links'),
        (snapshot) => onLinks(snapshot.docs.map(normalizeSignupLink)),
        onError,
    );
}

function newSecret() {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function createSignupLink(companyId, { label, target, startDate, endDate, maxUsers, permissions }, createdBy) {
    const secret = newSecret();
    const link = {
        target,
        start_date: Timestamp.fromDate(startDate),
        end_date: Timestamp.fromDate(endDate),
        max_users: maxUsers,
        permissions,
        revoked: false,
        created_at: serverTimestamp(),
        created_by: createdBy,
        users_signed_up: [],
    };
    if (label) link.label = label;

    await setDoc(doc(signupLinksCollection(companyId), secret), link);
    return secret;
}

// `changes` uses the stored field names; Dates are converted to Timestamps.
export function updateSignupLink(companyId, secret, changes) {
    const stored = Object.fromEntries(
        Object.entries(changes).map(([key, value]) => [
            key,
            value instanceof Date ? Timestamp.fromDate(value) : value,
        ]),
    );
    return updateDoc(doc(signupLinksCollection(companyId), secret), stored);
}

export function signupLinkUrl(companyName, companyId, secret) {
    return `${SIGNUP_BASE_URL}/${companySlug(companyName) || 'virksomhed'}/${companyId}-${secret}`;
}

export function signupLinkLabel(link) {
    if (link.label) return link.label;
    return `Tilmeldingslink · ${link.start_date.toLocaleDateString('da-DK', { timeZone: 'Europe/Copenhagen' })}`;
}
